/* oxlint-disable react-compiler/react-compiler */
// https://www.notion.so/doss-com/React-Compiler-Warning-Fixes-Log-34cf16c5ee36803c9817d41bdec112e1?source=copy_link
import { useApolloClient } from '@apollo/client'
import { checkRangeFilter } from '@doss/common-all'
import {
  GridColumnVisibilityModel,
  GridLogicOperator,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import _, { compact, get, isEmpty, isEqual, isNil, isUndefined, noop, omit, uniqBy } from 'lodash'
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  FilterOperator,
  GetTableRowsQuery,
  PaginatedTableRowsInput,
  SortDirection,
  TableFilterInput,
  TableSortInput,
  useGetBacklinkTableRowsLazyQuery,
  useGetBacklinkTableRowsQuery,
  useGetTableRowsLazyQuery,
  useGetTableRowsQuery,
} from '~/common/generated/apollo-operations'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTableLoading } from '~/common/hooks/useTableLoadingContext'
import { AdaptiveTableScroll, TABLE_SCROLL_CONFIG } from '~/common/utils/adaptive-scroll'
import { getDuplicateFilterErrorMessage } from '~/common/utils/duplicate-filter'
import { retrieveTableSearchString, storeTableSearchString } from '~/common/utils/local-storage'
import { trackTableFilters, trackTableSorts } from '~/common/utils/metrics'
import {
  clearAllTableRowsFromCache,
  getHasChangedFilters,
  getHasChangedSort,
  getHasChangedVisibleColumns,
  getNumRows,
} from '~/common/utils/table'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import { TableSource } from '../../shared/DossTable'
import {
  DossGridFilterModel,
  buildColumnDataTypeMapForGridFilters,
  dossGridFilterItemsToTableFilterInputs,
  filterOperatorToBooleanOperator,
  getFiltersFromTableTab,
  isValueLessFilterOperator,
  swapLoggedInUserFilterArgForUserId,
} from '../filters'
import { getSortsFromTableTab } from '../sorts'
import { getGridColumnVisibilityModel } from '../table-column'
import { useTableDetails } from '../TableDetailsProvider'
import { DetailedTableRow, TableType } from '../types'

type GridScrollPosition = ReturnType<GridApiPremium['getScrollPosition']>

interface TableActionsProps {
  parentRowId: string | undefined
  backlinkColumnId: string | undefined
  tableType: TableType
  tableSource: TableSource
  allowInfiniteScroll: boolean
  setHideToolbarButtons: (hide: boolean) => void
  apiRef: React.MutableRefObject<GridApiPremium>
  tableFilters?: TableFilterInput[] | null
  gridFilterModel?: DossGridFilterModel | null
  currentUserId: string
  parentTableColumnIds: string[]
}

/**
 * Hook for managing table actions like filtering, search, sorting, and pagination.
 */
export function useTableActions({
  parentRowId,
  backlinkColumnId,
  tableType,
  tableSource,
  allowInfiniteScroll,
  setHideToolbarButtons,
  apiRef,
  tableFilters,
  gridFilterModel,
  currentUserId,
  parentTableColumnIds,
}: TableActionsProps) {
  // Fetches need to be aborted when the component refetches data due to a change in filters, sorts, or search string
  // Or a change in table tab selection
  // If we don't abort these fetches, the data can be out of sync with the UI and display incorrect rows or counts for a specific table view
  const pageAbortController = useRef(new AbortController())

  // Abort any in-flight requests when the component unmounts to prevent stale updates
  // This is especially important for pivot table drilldowns which can be opened/closed rapidly
  useEffect(() => {
    return () => {
      pageAbortController.current.abort()
    }
  }, [])

  const client = useApolloClient()
  const { subscribe, unsubscribe } = useDossSignals()
  const {
    tableId,
    selectedTab,
    tableColumns,
    parentTableColumns,
    childTableColumns,
    tableTabs,
    setSelectedTabId,
    table,
  } = useTableDetails()
  const { permissionsManager } = useGlobalContext()
  const allColumns = useMemo(
    () => [...tableColumns, ...parentTableColumns, ...childTableColumns],
    [tableColumns, parentTableColumns, childTableColumns],
  )
  const { cleanedFilters, model: cleanedFilterModel } = useMemo(
    () =>
      tableFilters && gridFilterModel
        ? {
            cleanedFilters: tableFilters,
            model: gridFilterModel,
          }
        : getFiltersFromTableTab({
            selectedTab,
            tableColumns: allColumns,
            tableId,
          }),
    [selectedTab, allColumns, tableFilters, gridFilterModel, tableId],
  )
  const { cleanedSorts, gridSortItems } = useMemo(
    () => getSortsFromTableTab(selectedTab),
    [selectedTab],
  )

  const [rowsData, setRowsData] = useState<DetailedTableRow[]>([])
  const [rowCount, setRowCount] = useState(0)
  const [filters, setFilters] = useState<TableFilterInput[] | null>(cleanedFilters)
  const [draftFilterModel, setDraftFilterModel] = useState<DossGridFilterModel>(cleanedFilterModel)
  const [filterModel, setFilterModel] = useState<DossGridFilterModel>(cleanedFilterModel)
  const [sorts, setSorts] = useState<TableSortInput[] | null>(cleanedSorts)
  const [sortModel, setSortModel] = useState<GridSortModel>(gridSortItems)
  // Only primary table views persist their search text — embedded child/backlink tables and pivot
  // drilldowns must not be silently filtered by a search typed on the table page.
  const persistSearchString =
    tableType === TableType.TABLE && tableSource !== TableSource.PIVOT_TABLE_DRILLDOWN
  const [searchString, setSearchString] = useState<string | undefined>(() =>
    persistSearchString
      ? retrieveTableSearchString(currentUserId, tableId) || undefined
      : undefined,
  )

  // Track the current tab ID to detect tab changes
  const currentTabId = useRef<string | undefined>(selectedTab?.id)

  // Create a map of column id to column data type (table columns + system-generated columns).
  // Placed early so draftFiltersDerived and effectiveFilters can use it.
  const columnDataTypeMap = useMemo(
    () => buildColumnDataTypeMapForGridFilters(allColumns),
    [allColumns],
  )

  // Derive TableFilterInput[] from the draft panel so subscription query always matches the UI.
  // State (filters) updates async; without this we can fire a 1-filter query after adding a system column filter.
  const draftFiltersDerived = useMemo(() => {
    const model = draftFilterModel
    if (!model.items.length) {
      return null
    }
    return dossGridFilterItemsToTableFilterInputs(model.items, columnDataTypeMap)
  }, [draftFilterModel, columnDataTypeMap])

  const snackbar = useDossSnackbar()
  const { updateTableLoading } = useTableLoading()
  const [getTableRows, { loading: tableRowsLoading }] = useGetTableRowsLazyQuery()
  const [getBacklinkTableRows, { loading: backlinkTableRowsLoading }] =
    useGetBacklinkTableRowsLazyQuery()

  // Use draft-derived filters when the panel has items so the subscription query matches the UI
  // (state updates async; otherwise we fire a query with fewer filters right after adding a system column).
  const effectiveFilters =
    (draftFiltersDerived && draftFiltersDerived.length > 0 ? draftFiltersDerived : null) ??
    filters ??
    cleanedFilters
  const effectiveSorts = sorts ?? cleanedSorts
  const effectiveLogicOperator = filterModel.logicOperator ?? cleanedFilterModel.logicOperator

  const isPivotTableDrilldown = tableSource === TableSource.PIVOT_TABLE_DRILLDOWN
  const isBacklinkTable = tableType === TableType.BACKLINK_TABLE

  // For the initial query, do not pass in any filters or sorts unless viewing the table from
  // the pivot table drilldown view or a backlink table. For non-pivot table drilldown views or
  // backlink tables, the initial load's filters and sorts will be handled by the backend. This
  // ensures that filters and sorts on the tab are applied for the user even if they do not have
  // permissions to view the column that is being filtered on or sorted on.
  const baseInputs = {
    tableId,
    filters: isPivotTableDrilldown
      ? swapLoggedInUserFilterArgForUserId(effectiveFilters, currentUserId)
      : undefined,
    sorts: isPivotTableDrilldown ? effectiveSorts : undefined,
    filterBooleanOperator: filterOperatorToBooleanOperator(effectiveLogicOperator),
    pagination: {
      limit: getNumRows(tableSource),
      offset: 0,
    },
  }
  // Check if tab has changed
  const tabChanged = currentTabId.current !== selectedTab?.id
  if (tabChanged) {
    currentTabId.current = selectedTab?.id
  }

  let baseSkip = false
  if (isPivotTableDrilldown) {
    baseSkip =
      !tableId || !allColumns.length || (!!table?.parentTable && !parentTableColumns.length)
  } else {
    baseSkip =
      !tableId ||
      !selectedTab ||
      !allColumns.length ||
      !tableTabs.some((tab) => tab.id === selectedTab.id) ||
      // Always skip the eager query while a search is active.
      !!searchString ||
      // Only skip initial queries if user has customizations AND tab hasn't changed
      // This allows tab changes to reset customizations and load fresh data
      (!tabChanged && (!isEqual(filters, cleanedFilters) || !isEqual(sorts, cleanedSorts)))
  }

  // We want to fetch the initial page of the rows with the selected tab's filters and sorts
  // So we don't trigger the initial fetch until all of the necessary data is availables
  const { data: initialDataLoad, error: initialDataError } = useGetTableRowsQuery({
    variables: {
      input: {
        ...baseInputs,
        parentRowId: parentRowId ?? undefined,
        tableTabId: isPivotTableDrilldown ? undefined : (selectedTab?.id ?? ''),
      },
    },
    // Pivot drilldown uses cell-driven filters; eager fetch should not wait on selectedTab (see baseSkip).
    skip: isBacklinkTable || baseSkip || (!isPivotTableDrilldown && !selectedTab),
    fetchPolicy: 'cache-and-network',
  })
  const { data: initialBacklinkDataLoad, error: initialBacklinkDataError } =
    useGetBacklinkTableRowsQuery({
      variables: {
        input: {
          ...omit(baseInputs, 'filters', 'sorts'),
          filters: swapLoggedInUserFilterArgForUserId(cleanedFilters, currentUserId),
          sorts: cleanedSorts,
          backlinkColumnId: backlinkColumnId ?? '',
          backlinkRowId: parentRowId ?? '',
        },
      },
      skip: !isBacklinkTable || !backlinkColumnId || baseSkip,
      fetchPolicy: 'cache-and-network',
    })

  useEffect(() => {
    const data =
      initialDataLoad?.paginatedTableRows ?? initialBacklinkDataLoad?.paginatedBacklinkRows

    if (!data) {
      return
    }

    setRowsData(
      data.rows.map((row) => ({
        ...row,
        currentButtonNodes: [],
        processingWorkspaceButton: null,
      })),
    )
    setRowCount(data.totalCount)
  }, [initialDataLoad, initialBacklinkDataLoad])

  // Use a ref to store the current query variables
  const currentQueryVariables = useRef<PaginatedTableRowsInput | null>(null)

  const rowFetchCount = useMemo(() => {
    return getNumRows(tableSource)
  }, [tableSource])

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    () => {
      if (tableSource === TableSource.PIVOT_TABLE_DRILLDOWN) {
        // Create an object with all tableColumns visible
        return tableColumns.reduce<Record<string, boolean>>((acc, column) => {
          // Do not include parent table columns in the column visibility model
          if (parentTableColumnIds.includes(column.id)) {
            acc[column.id] = false
            return acc
          }
          acc[column.id] = true
          return acc
        }, {})
      }

      // Default empty object for other table sources
      return {}
    },
  )
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: rowFetchCount,
  })
  const tabVisibilityModel: GridColumnVisibilityModel = useMemo(() => {
    if (!selectedTab || !tableColumns.length) {
      return {}
    }
    // Use empty object so saved state reflects only what's on the tab (enables change detection for system columns)
    return getGridColumnVisibilityModel(tableType, {}, tableColumns, selectedTab.columns)
  }, [tableType, selectedTab, tableColumns])

  const [savedColumnVisibilityModel, setSavedColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(() => ({}))

  // This effect is intended to unhide the toolbar buttons after the user switches tabs or navigates to another table
  // The buttons get hidden to avoid flickering of the dirty state while the filters and sorts are transitioning
  // Once the load is complete, and the new tab is synced with the filter, sort, and column visibility states, we unhide the buttons
  useEffect(() => {
    if (!selectedTab || !tableTabs.length || !tableTabs.some((tab) => tab.id === selectedTab.id)) {
      return
    }
    const hasChangedSort = getHasChangedSort(selectedTab, sortModel)
    const hasChangedFilters = getHasChangedFilters(
      tableColumns,
      selectedTab,
      filterModel,
      tableId,
      permissionsManager,
    )
    const hasChangedVisibleColumns = getHasChangedVisibleColumns(
      columnVisibilityModel,
      tabVisibilityModel,
    )

    if (!(hasChangedSort && hasChangedFilters && hasChangedVisibleColumns)) {
      setHideToolbarButtons(false)
    }
  }, [
    sorts,
    tableColumns,
    tabVisibilityModel,
    columnVisibilityModel,
    sortModel,
    filterModel,
    selectedTab,
    tableTabs,
    setHideToolbarButtons,
    permissionsManager,
    tableId,
    tableType,
  ])

  // Reset sorts and filters only when the user navigates to a different table or tab.
  // Do not sync from tab when selectedTab/tableTabs are just new references (e.g. Apollo refetch),
  // or we would overwrite the user's draft filters and trigger a second query with fewer filters.
  useEffect(() => {
    // Reset current query variables so new data will be loaded for the new table/tab
    currentQueryVariables.current = null

    if (tableSource === TableSource.PIVOT_TABLE_DRILLDOWN && tableFilters && gridFilterModel) {
      setFilters(tableFilters)
      setFilterModel(gridFilterModel)
      setDraftFilterModel(gridFilterModel)
      setSortModel([])
      setSorts(null)
      return
    }

    if (!selectedTab || !tableColumns.length) {
      setSorts(null)
      setSortModel([])
      setFilters(null)
      setFilterModel({ items: [] })
      setDraftFilterModel({ items: [] })
      setColumnVisibilityModel({})
      setSavedColumnVisibilityModel({})
    } else if (tableTabs.some((tab) => tab.id === selectedTab.id)) {
      const tab = tableTabs.find((tab) => tab.id === selectedTab.id)
      const { cleanedFilters: newFilters, model: newFilterModel } = getFiltersFromTableTab({
        selectedTab: tab,
        tableColumns: allColumns,
        tableId,
      })

      const { cleanedSorts: newSorts, gridSortItems: newGridSortItems } = getSortsFromTableTab(tab)
      setSorts(newSorts)
      setSortModel(newGridSortItems)
      setFilters(newFilters)
      setFilterModel(newFilterModel)
      setDraftFilterModel(newFilterModel)
      setColumnVisibilityModel(tabVisibilityModel)
      setSavedColumnVisibilityModel(tabVisibilityModel)

      // Tab state reset - initial queries will re-run due to baseSkip logic

      // The search string survives tab changes,so fetch the new
      // tab's first page with the search applied through handleQuery instead
      if (searchString) {
        handleResetPagination()
        handleQuery({
          shouldResetRowsData: true,
          newSearchString: searchString,
          newPaginationModel: { page: 0, pageSize: rowFetchCount },
          filter: {
            cleanedFilters: newFilters,
            logicOperator: newFilterModel.logicOperator,
          },
          cleanedSorts: newSorts,
        })
      }

      if (
        !isEqual(tab?.filters, selectedTab.filters) ||
        !isEqual(tab?.sorts, selectedTab.sorts) ||
        !isEqual(tab?.columns, selectedTab.columns)
      ) {
        setSelectedTabId(selectedTab.id)
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, selectedTab?.id, selectedTab, tableTabs, tableColumns])

  const handleResetPagination = useCallback(() => {
    const newPaginationModel = { page: 0, pageSize: rowFetchCount }
    setPaginationModel(newPaginationModel)
  }, [rowFetchCount])

  // This is the paginated row fetch that uses filters and sorts it is given if they are different
  // from the filters and sorts on the selected tab, or that of the selected tab by default.
  // It also fetches the page it needs from the pagination model.
  const handleQuery = useCallback(
    (props: {
      newSearchString?: string
      filter?: {
        cleanedFilters: TableFilterInput[]
        logicOperator?: GridLogicOperator
      }
      cleanedSorts?: TableSortInput[]
      newPaginationModel?: GridPaginationModel
      shouldResetRowsData: boolean
      forceRefetch?: boolean
    }) => {
      if (
        tableSource !== TableSource.PIVOT_TABLE_DRILLDOWN &&
        (!tableId ||
          !selectedTab ||
          !tableColumns.length ||
          !tableTabs.some((tab) => tab.id === selectedTab.id))
      ) {
        return
      }

      if (!selectedTab && tableSource !== TableSource.PIVOT_TABLE_DRILLDOWN) {
        return
      }

      const querySearchString = props.newSearchString ?? searchString
      const queryFilters = props.filter?.cleanedFilters ?? filters
      const queryFilterLogicOperator = props.filter?.logicOperator ?? filterModel.logicOperator
      const querySorts = props.cleanedSorts ?? sorts
      const queryPaginationModel = props.newPaginationModel ?? paginationModel

      const queryParams: PaginatedTableRowsInput = {
        tableId,
        filters: swapLoggedInUserFilterArgForUserId(queryFilters ?? [], currentUserId),
        sorts: querySorts ?? [],
        pagination: {
          limit: rowFetchCount,
          offset: queryPaginationModel.page * queryPaginationModel.pageSize,
        },
        filterBooleanOperator: filterOperatorToBooleanOperator(queryFilterLogicOperator),
        parentRowId: parentRowId ?? undefined,
        searchString: querySearchString || undefined, // Only include if not empty
      }

      const shouldFetch = props.forceRefetch || !isEqual(queryParams, currentQueryVariables.current)

      if (!shouldFetch) {
        return
      }

      // Store the current query variables
      const prevQueryVariables = currentQueryVariables.current
      currentQueryVariables.current = queryParams

      // Evict Apollo cache when filters, sorts, or search changes (not just pagination)
      // This prevents unbounded cache growth as users navigate through different filter/sort combinations
      const filtersChanged = !isEqual(prevQueryVariables?.filters, queryParams.filters)
      const sortsChanged = !isEqual(prevQueryVariables?.sorts, queryParams.sorts)
      const searchChanged = prevQueryVariables?.searchString !== queryParams.searchString
      const tabChanged = prevQueryVariables?.parentRowId !== queryParams.parentRowId

      if (filtersChanged || sortsChanged || searchChanged || tabChanged) {
        clearAllTableRowsFromCache(client.cache)
      }

      // Once the rows are fetched, update the row count and rows data
      const onCompleted = (data?: GetTableRowsQuery['paginatedTableRows']) => {
        if (!data) {
          return
        }

        // Use startTransition to make the re-render interruptible, preventing UI freezes
        // during state updates
        startTransition(() => {
          setRowCount(data.totalCount)
          if (props.shouldResetRowsData) {
            setRowsData([...data.rows])
          } else {
            // When infinite scroll is enabled, append the new rows to the existing rows when scrolling
            setRowsData((prevRows) => {
              return uniqBy([...prevRows, ...data.rows], (row) => row.id)
            })
          }
        })
      }

      // Abort the previous fetch if it exists just before creating the new one
      pageAbortController.current.abort()
      pageAbortController.current = new AbortController()
      const pageAbortSignal = pageAbortController.current.signal

      if (tableType !== TableType.BACKLINK_TABLE) {
        getTableRows({
          variables: { input: queryParams },
          fetchPolicy: props.forceRefetch ? 'network-only' : 'cache-first',
          context: { fetchOptions: { signal: pageAbortSignal } },
        })
          .then(({ data, error }) => {
            // Skip processing if request was aborted
            if (pageAbortSignal.aborted) {
              return
            }
            if (error) {
              // Don't show error for aborted requests (e.g., when a new query supersedes this one)
              if (error.name === 'AbortError' || error.message.includes('aborted')) {
                return
              }
              snackbar.showError(
                getDuplicateFilterErrorMessage(error) ?? 'Error fetching table rows',
              )
              return
            }
            onCompleted(data?.paginatedTableRows)
          })
          .finally(() => updateTableLoading(false))
      } else if (backlinkColumnId && parentRowId) {
        const input = {
          ...omit(queryParams, 'parentRowId'),
          backlinkColumnId,
          backlinkRowId: parentRowId,
          filters: swapLoggedInUserFilterArgForUserId(queryFilters ?? [], currentUserId),
          sorts: querySorts ?? [],
        }
        getBacklinkTableRows({
          variables: { input },
          context: { fetchOptions: { signal: pageAbortSignal } },
        })
          .then(({ data, error }) => {
            // Skip processing if request was aborted
            if (pageAbortSignal.aborted) {
              return
            }
            if (error) {
              // Don't show error for aborted requests (e.g., when a new query supersedes this one)
              if (error.name === 'AbortError' || error.message.includes('aborted')) {
                return
              }
              snackbar.showError(
                getDuplicateFilterErrorMessage(error) ?? 'Error fetching table rows',
              )
              return
            }
            if (!data) {
              return
            }
            onCompleted({
              ...data.paginatedBacklinkRows,
              rows: data.paginatedBacklinkRows.rows.map((row) => ({
                ...row,
                currentButtonNodes: [],
                processingWorkspaceButton: null,
              })),
            })
          })
          .finally(() => updateTableLoading(false))
      }
    },
    // Only change the page fetch callback when the relevant query variables change
    // That is, the filters sorts and search string, and the page to fetch
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [currentUserId, filterModel.logicOperator, filters, paginationModel, searchString, sorts],
  )

  // Keep a stable reference to the latest handleQuery function to prevent stale closures
  // in subscription handlers. Without this, real-time updates (like row changes) would
  // call an outdated version of handleQuery and revert the table to initial state,
  // losing any user-applied filters, sorts, or pagination.
  const handleQueryRef = useRef(handleQuery)

  // Update the ref whenever handleQuery changes
  useEffect(() => {
    handleQueryRef.current = handleQuery
  }, [handleQuery])

  // Called when the reset button is clicked to revert to the selected tab's filters, sorts, and columns
  const resetSelectedTab = useCallback(() => {
    const tab = tableTabs.find((tab) => tab.id === selectedTab?.id)
    const { cleanedFilters: newFilters, model: newFilterModel } = getFiltersFromTableTab({
      selectedTab: tab,
      tableColumns: allColumns,
      tableId,
    })

    const { cleanedSorts: newSorts, gridSortItems: newGridSortItems } = getSortsFromTableTab(tab)
    setSorts(newSorts)
    setSortModel(newGridSortItems)
    setFilters(newFilters)
    setFilterModel(newFilterModel)
    setDraftFilterModel(newFilterModel)
    setColumnVisibilityModel(tabVisibilityModel)
    setSavedColumnVisibilityModel(tabVisibilityModel)

    handleQuery({
      shouldResetRowsData: true,
      cleanedSorts: newSorts,
      filter: { cleanedFilters: newFilters },
    })
    // No need for any more dependencies here because handleQuery
    // already has the necessary dependencies
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, handleQuery])

  const handleFilterChange = useCallback(
    (model: DossGridFilterModel, tableName: string) => {
      const cleanedFilters = dossGridFilterItemsToTableFilterInputs(model.items, columnDataTypeMap)

      setDraftFilterModel(model)

      const isDraftModelComplete = cleanedFilters.every((item) => {
        const columnDataType = get(columnDataTypeMap, item.columnId, undefined)
        if (!columnDataType) {
          return undefined
        }
        // Value-less operators do not have a filter string or filter array
        if (isValueLessFilterOperator(item.operator)) {
          return true
        } else if (
          item.operator === FilterOperator.IS_ANY_OF ||
          item.operator === FilterOperator.IS_NONE_OF
        ) {
          // For IS_ANY_OF and IS_NONE_OF, there must be at least one value provided in the array
          return !isEmpty(item.filterArray)
        } else if (
          item.operator === FilterOperator.RANGE ||
          item.operator === FilterOperator.NOT_IN_RANGE
        ) {
          if (!item.operand) {
            return false
          }
          return checkRangeFilter(columnDataType, item.operand)
        } else if (item.filterString) {
          // For all other filters, make sure the value is not an empty string or empty array
          return !isEmpty(item.filterString)
        } else if (item.filterArray) {
          return !isEmpty(item.filterArray)
        }
        // If there is no filter string or filter array, return false
        return false
      })

      if (isDraftModelComplete) {
        setFilterModel(model)
        // Query for data only if the filters or the filter operator has changed

        if (
          !isEqual(cleanedFilters, filters) ||
          !isEqual(model.logicOperator, filterModel.logicOperator)
        ) {
          setFilters(cleanedFilters)
          trackTableFilters({
            tableName,
            filters: swapLoggedInUserFilterArgForUserId(cleanedFilters, currentUserId),
          })

          // Reset pagination if in infinite scroll mode
          if (allowInfiniteScroll) {
            handleResetPagination()
          }

          // Re-query the table rows with the updated filters only if the filters are valid and
          // have changed
          handleQuery({
            shouldResetRowsData: true,
            filter: {
              cleanedFilters: swapLoggedInUserFilterArgForUserId(cleanedFilters, currentUserId),
              logicOperator: model.logicOperator,
            },
          })
        }
      }
    },
    [
      currentUserId,
      filters,
      handleQuery,
      allowInfiniteScroll,
      handleResetPagination,
      columnDataTypeMap,
      filterModel.logicOperator,
    ],
  )

  const handleSortChange = useCallback(
    (model: GridSortModel, tableName: string) => {
      const cleanedSorts: TableSortInput[] = compact(
        model.map((item) => {
          if (isNil(item.sort)) {
            return undefined
          }
          return {
            columnId: item.field,
            direction: item.sort === 'asc' ? SortDirection.ASC : SortDirection.DESC,
          }
        }),
      )

      if (!isEqual(cleanedSorts, sorts)) {
        setSorts(cleanedSorts)

        trackTableSorts({
          sorts: cleanedSorts,
          tableName,
        })
      }
      setSortModel(model)

      // Reset pagination if in infinite scroll mode
      if (allowInfiniteScroll) {
        handleResetPagination()
      }

      // Re-query the table rows with the updated sorts
      handleQuery({ shouldResetRowsData: true, cleanedSorts })
    },
    [handleQuery, sorts, allowInfiniteScroll, handleResetPagination],
  )

  const handleSearchChange = useCallback(
    (newSearchString: string) => {
      setSearchString(newSearchString)

      // Persist the search text so it is restored after a page refresh (per user, per table)
      if (persistSearchString) {
        storeTableSearchString(currentUserId, tableId, newSearchString)
      }

      // If in infinite scroll mode, reset the pagination
      if (allowInfiniteScroll) {
        handleResetPagination()
      }

      // Re-query the table rows with the updated search string
      handleQuery({ shouldResetRowsData: true, newSearchString })
    },
    [
      handleQuery,
      allowInfiniteScroll,
      handleResetPagination,
      persistSearchString,
      currentUserId,
      tableId,
    ],
  )

  const handlePaginationChange = useCallback(
    (newPaginationModel: GridPaginationModel) => {
      setPaginationModel(newPaginationModel)

      // Re-query the table rows with the updated pagination
      handleQuery({ shouldResetRowsData: true, newPaginationModel })
    },
    [handleQuery],
  )

  const handleChildTableInitialLoad = useCallback(() => {
    updateTableLoading(true)
    handleQuery({ shouldResetRowsData: true })
    // Child tables have table tabs but no table tab columns, so we will not set the table tab here.
  }, [handleQuery, updateTableLoading])

  /**
   * Adaptive infinite scroll logic to dynamically set the re-fetch trigger point higher or lower
   * based on the user's scroll behavior to prevent user waiting for data to load.
   */
  const adaptiveScroll = useRef(new AdaptiveTableScroll(tableSource))
  const handleScroll = useCallback(
    (scrollPosition: GridScrollPosition) => {
      // Don't do anything if infinite scroll is disabled
      if (!allowInfiniteScroll) {
        return
      }

      // Only load more data if we haven't loaded everything
      if (rowsData.length === rowCount) {
        return
      }

      const api = apiRef.current as GridApiPremium | null

      if (!api) {
        return
      }

      const dimensions = api.getRootDimensions()

      // If we don't have scroll dimensions, we can't determine if we should load more
      if (!dimensions || !dimensions.hasScrollY) {
        return
      }

      // Get row height and calculate total content height
      const rowHeight = api.unstable_getRowHeight(0)
      const estimatedTotalHeight = rowsData.length * rowHeight

      // Let the adaptive scroll handler determine if we should load more
      const scrollState = adaptiveScroll.current.updateScrollState({
        scrollTop: scrollPosition.top,
        scrollHeight: estimatedTotalHeight,
        clientHeight: dimensions.viewportInnerSize.height,
      } as HTMLElement)

      // If we've reached the trigger point, load more data
      if (scrollState.reachedTriggerPoint && !tableRowsLoading && !backlinkTableRowsLoading) {
        updateTableLoading(true)

        // Calculate new page based on table batch size
        const newPage = Math.floor(
          rowsData.length / TABLE_SCROLL_CONFIG.BASE_FETCH_SIZES[tableSource],
        )

        handleQuery({
          shouldResetRowsData: false,
          newPaginationModel: {
            page: newPage,
            pageSize: TABLE_SCROLL_CONFIG.BASE_FETCH_SIZES[tableSource],
          },
        })
      }
    },
    [
      allowInfiniteScroll,
      apiRef,
      rowCount,
      rowsData.length,
      tableRowsLoading,
      backlinkTableRowsLoading,
      tableSource,
      updateTableLoading,
      handleQuery,
    ],
  )

  /**
   * Subscribe to scroll position changes to trigger infinite scroll
   */
  useEffect(() => {
    // Don't do anything if infinite scroll is disabled
    if (!allowInfiniteScroll) {
      return
    }

    // Create a debounced scroll handler that fires at most once every 50ms
    // leading = true means the first call will be immediate (not debounced)
    const debouncedScroll = _.debounce(
      (scrollPosition: GridScrollPosition) => {
        handleScroll(scrollPosition)
      },
      50,
      { leading: true, trailing: true },
    )

    // Add safety check to ensure apiRef.current is valid and has the subscribeEvent method
    const api = apiRef.current as GridApiPremium | null

    if (!api || typeof api.subscribeEvent !== 'function') {
      // Return a no-op cleanup if we can't subscribe yet
      return noop
    }

    const unsubscribe = api.subscribeEvent('scrollPositionChange', (scrollPosition) => {
      debouncedScroll(scrollPosition)
    })

    return () => {
      debouncedScroll.cancel()
      unsubscribe()
    }
  }, [apiRef, handleScroll, allowInfiniteScroll])

  // Trigger initial fetch for pivot table drilldowns.
  // Uses handleQueryRef instead of handleQuery to prevent re-running when handleQuery
  // is recreated due to filter state settling. The ref is updated by effect above,
  // so it always points to the latest handleQuery. This prevents the abort error
  // that occurred when the effect fired twice during state initialization.
  useEffect(() => {
    if (tableSource === TableSource.PIVOT_TABLE_DRILLDOWN) {
      handleQueryRef.current({ shouldResetRowsData: true })
    }
  }, [tableSource])

  useEffect(() => {
    const subscriptionId = subscribe(DossSignalType.REFETCH_TABLE_RECORDS, async (id) => {
      if (id === tableId) {
        handleQuery({ shouldResetRowsData: true, forceRefetch: true })
      }
    })

    return () => {
      unsubscribe(subscriptionId)
    }
    // Only resubscribe when we have a new tableId or handleQuery due
    // to search params like filters, sorts, pagination, and search string
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [handleQuery, tableId])

  // Mirror the skip props of the two eager initial-load queries so we only consider an error
  // from the query variant that is actually active for this table
  const rowsQueryActive = !isBacklinkTable && !baseSkip && (isPivotTableDrilldown || !!selectedTab)
  const backlinkQueryActive = isBacklinkTable && !!backlinkColumnId && !baseSkip

  // If an eager initial-load query failed with no data, rows never arrive and initialLoading
  // would stay true forever (indefinite loading bar). Throw so DossTable's error boundary
  // renders an error state with retry. When cache-and-network served cached rows alongside a
  // network error, keep rendering the cached data instead of throwing.
  if (rowsQueryActive && isUndefined(initialDataLoad) && initialDataError) {
    throw initialDataError
  }
  if (backlinkQueryActive && isUndefined(initialBacklinkDataLoad) && initialBacklinkDataError) {
    throw initialBacklinkDataError
  }

  return {
    // Row data
    rowsData,
    setRowsData,

    // Row count
    rowCount,
    setRowCount,

    // Filters
    filters,
    setDraftFilterModel,
    draftFilterModel,
    filterModel,
    setFilterModel,
    handleFilterChange,

    // Sorts
    sortModel,
    sorts,
    handleSortChange,

    // Search
    searchString,
    handleSearchChange,
    setSearchString, // Export the setter for external clearing

    // Column visibility
    columnVisibilityModel,
    setColumnVisibilityModel,
    savedColumnVisibilityModel,
    onTableTabColumnsSaved: setSavedColumnVisibilityModel,

    // Pagination
    paginationModel,
    handlePaginationChange,

    // Child tables may not have a tab so we need to handle the initial load
    handleChildTableInitialLoad,

    // Table is loading rows if the page fetch or initial fetch is loading
    // Only consider initial data undefined as loading if baseSkip is false (query is active)
    // This prevents showing loading state when initial queries are skipped due to custom filters/sorts
    tableRowsLoading:
      tableRowsLoading ||
      backlinkTableRowsLoading ||
      (isUndefined(initialDataLoad) && isUndefined(initialBacklinkDataLoad) && !baseSkip),
    initialLoading:
      isUndefined(initialDataLoad) && isUndefined(initialBacklinkDataLoad) && !baseSkip,

    // Used when reset is clicked and need to revert to the selected tab's filters, sorts, and columns
    resetSelectedTab,
    handleQueryRef,
  }
}
